import React, { useState, useMemo, useEffect, useRef } from 'react'
import { Link, router } from '@inertiajs/react'
import OperatorLayout from '@/layouts/OperatorLayout'
import PricingRulesNav from '@/components/pricing-rules/PricingRulesNav'
import RoomTypeModal, {
  type LinkState,
  type RoomDraft,
} from '@/components/pricing-rules/RoomTypeModal'
import DerivedRatePlansModal from '@/components/pricing-rules/DerivedRatePlansModal'
import { Button } from '@/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import RoomsSetupSaveError, {
  parseRoomsSetupErrors,
  type RoomsSetupSaveErrorItem,
} from '@/components/pricing-rules/RoomsSetupSaveError'
import StructureSyncControl, { StructureSyncMeta } from '@/components/StructureSyncControl'
import { getRelativeTime } from '@/components/DataFreshnessIndicator'
import { BedDouble, ChevronRight, History, Link2 } from 'lucide-react'
import { currencySymbol } from '@/lib/calendar-utils'
import { formatMoneySym } from '@/lib/format'
import { prefillSuggestedRates, sampleEvidenceLabel } from '@/lib/suggestions'
import { nestRoomRows } from '@/lib/room-type-tree'
import type { RoomsSetupProps, RoomsSetupRoomType } from '@/types/shared'
import { inertiaSave, usePricingReview } from '@/hooks/usePricingReview'

interface BoundsState {
  min: string
  max: string
}

interface DerivedPlanInput {
  offset_type: 'percentage' | 'absolute'
  offset_value: string
}

interface LeadPlanState {
  leadRatePlanId: number | null
  derivedPlans: Record<string, DerivedPlanInput>
}

// The child-link glyph the product owner specified for indented rows.
const LINK_GLYPH = 'M19 15l-6 6-1.42-1.42L15.17 16H4V4h2v10h9.17l-3.59-3.58L13 9l6 6z'

// THE BUILDING'S ROOMS (owner ruling 2026-10-26). This page answers one question, what each room in
// the building is worth, so at a combined inventory it lists the building's rooms exactly as an
// ordinary hotel's page lists its own: the server leaves out Same rooms, counts a twin as the building
// does and names a room's listing only where two rows would otherwise read the same. Same rooms, rate
// plan pairing and Same room offsets are set in the room mapping on the Combined inventory page.
export default function RoomsSetup({
  currentUser,
  property,
  properties,
  baseRates,
  roomTypes,
  baseRateSuggestions,
  structureSync,
  roomMappingHref,
}: RoomsSetupProps) {
  const sym = currencySymbol(property.currency)

  // --- State initialization helpers ---
  function buildRates() {
    const result: Record<string, Record<string, string>> = {}
    for (const rt of roomTypes) {
      result[rt.eviivoRoomTypeId] = {}
      for (let occ = 1; occ <= rt.maxOccupancy; occ++) {
        const key = String(occ)
        const value = baseRates[rt.eviivoRoomTypeId]?.[key]
        result[rt.eviivoRoomTypeId][key] = value != null ? String(value) : ''
      }
    }
    return result
  }

  function buildBounds() {
    const result: Record<string, BoundsState> = {}
    for (const rt of roomTypes) {
      result[String(rt.id)] = {
        min: rt.minRate != null ? String(rt.minRate) : '',
        max: rt.maxRate != null ? String(rt.maxRate) : '',
      }
    }
    return result
  }

  function buildLeadPlans(): Record<string, LeadPlanState> {
    const result: Record<string, LeadPlanState> = {}
    for (const rt of roomTypes) {
      const derived: Record<string, DerivedPlanInput> = {}
      for (const rp of rt.ratePlans || []) {
        if (rp.id !== rt.leadRatePlanId && rp.offsetType && rp.offsetValue != null) {
          derived[String(rp.id)] = {
            offset_type: rp.offsetType === 'percentage' ? 'percentage' : 'absolute',
            offset_value: String(rp.offsetValue),
          }
        }
      }
      result[String(rt.id)] = {
        leadRatePlanId: rt.leadRatePlanId,
        derivedPlans: derived,
      }
    }
    return result
  }

  function buildLinks(): Record<string, LinkState> {
    const result: Record<string, LinkState> = {}
    for (const rt of roomTypes) {
      result[String(rt.id)] = {
        parentRoomTypeId: rt.parentRoomTypeId,
        // Preserve an explicit absolute link; default everything else to %
        // (industry standard) so newly-linked rooms start on percentage.
        offsetType: rt.parentOffsetType === 'absolute' ? 'absolute' : 'percentage',
        offsetValue: rt.parentOffsetValue != null ? String(rt.parentOffsetValue) : '',
      }
    }
    return result
  }

  // Local editing state
  const [rates, setRates] = useState(buildRates)
  const [bounds, setBounds] = useState(buildBounds)
  const [leadPlans, setLeadPlans] = useState<Record<string, LeadPlanState>>(buildLeadPlans)
  const [links, setLinks] = useState<Record<string, LinkState>>(buildLinks)

  // Fingerprint of server-side data — changes after every save/action redirect
  const serverFingerprint = useMemo(
    () =>
      JSON.stringify({
        br: baseRates,
        rts: roomTypes.map((rt) => ({
          id: rt.id,
          min: rt.minRate,
          max: rt.maxRate,
          lead: rt.leadRatePlanId,
          parent: rt.parentRoomTypeId,
          pot: rt.parentOffsetType,
          pov: rt.parentOffsetValue,
          plans: rt.ratePlans.map((rp) => ({ id: rp.id, t: rp.offsetType, v: rp.offsetValue })),
        })),
      }),
    [baseRates, roomTypes]
  )

  // Re-sync local state from props whenever server data changes
  useEffect(() => {
    setRates(buildRates())
    setBounds(buildBounds())
    setLeadPlans(buildLeadPlans())
    setLinks(buildLinks())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverFingerprint])

  const [saving, setSaving] = useState(false)
  const savingRef = useRef(false)
  // The server review replaces the old "Confirm rate recompute" dialog (change safety §5.17.1): it
  // names the hotel, the business and every value that moves, where the old one only counted rooms.
  const review = usePricingReview()

  // A failed save is ATOMIC — nothing was written — and the staged edits above survive it
  // (preserveState is Inertia's default for PATCH, and the server fingerprint didn't change,
  // so the re-sync effect never fires). These are the backend-composed per-room sentences,
  // rendered in an inline panel by the save bar; they persist until dismissed, edited around,
  // or the next save attempt. Never a toast — a batch failure the operator must fix
  // room-by-room cannot live in a surface that vanishes.
  const [saveErrors, setSaveErrors] = useState<RoomsSetupSaveErrorItem[] | null>(null)
  const saveErrorRef = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    if (saveErrors) saveErrorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
  }, [saveErrors])

  // Modal state
  const [editingRoomId, setEditingRoomId] = useState<number | null>(null)
  const editingRoom = editingRoomId
    ? roomTypes.find((rt) => rt.id === editingRoomId)
    : null

  // When the operator clicks a suggestion chip, the room editor opens with the
  // suggested amount pre-typed into the (empty) base-rate field. The prefill
  // lives only in the editor's draft — cancelling discards it, and Apply merely
  // stages it like any hand-typed value (nothing saves until Review & Save).
  const [suggestionPrefillRoomId, setSuggestionPrefillRoomId] = useState<number | null>(null)

  function openEditorWithSuggestion(roomId: number) {
    setSuggestionPrefillRoomId(roomId)
    setEditingRoomId(roomId)
  }

  function editorRatesFor(rt: RoomsSetupRoomType): Record<string, string> {
    const current = rates[rt.eviivoRoomTypeId] || {}
    const suggested = baseRateSuggestions[String(rt.id)]?.suggestedAmount
    if (suggestionPrefillRoomId !== rt.id || suggested == null) return current
    return prefillSuggestedRates(current, suggested)
  }

  // Derived rate plans modal state
  const [derivedRatePlansRoomId, setDerivedRatePlansRoomId] = useState<number | null>(null)
  const derivedRatePlansRoom = derivedRatePlansRoomId
    ? roomTypes.find((rt) => rt.id === derivedRatePlansRoomId)
    : null
  const derivedRatePlansLocal = derivedRatePlansRoomId
    ? leadPlans[String(derivedRatePlansRoomId)]
    : null

  // --- Dirty / change detection ---
  function originalDerivedFor(rt: RoomsSetupRoomType) {
    const originalDerived: Record<string, DerivedPlanInput> = {}
    for (const rp of rt.ratePlans || []) {
      if (rp.id !== rt.leadRatePlanId && rp.offsetType && rp.offsetValue != null) {
        originalDerived[String(rp.id)] = {
          offset_type: rp.offsetType === 'percentage' ? 'percentage' : 'absolute',
          offset_value: String(rp.offsetValue),
        }
      }
    }
    return originalDerived
  }

  function rateChanged(rt: RoomsSetupRoomType) {
    for (let occ = 1; occ <= rt.maxOccupancy; occ++) {
      const key = String(occ)
      const original = baseRates[rt.eviivoRoomTypeId]?.[key]
      const current = rates[rt.eviivoRoomTypeId]?.[key]
      if (current !== (original != null ? String(original) : '')) return true
    }
    return false
  }

  function boundChanged(rt: RoomsSetupRoomType) {
    const key = String(rt.id)
    const origMin = rt.minRate != null ? String(rt.minRate) : ''
    const origMax = rt.maxRate != null ? String(rt.maxRate) : ''
    return bounds[key]?.min !== origMin || bounds[key]?.max !== origMax
  }

  function leadChanged(rt: RoomsSetupRoomType) {
    const local = leadPlans[String(rt.id)]
    if (!local) return false
    if (local.leadRatePlanId !== rt.leadRatePlanId) return true
    const orig = originalDerivedFor(rt)
    const localKeys = Object.keys(local.derivedPlans)
    const origKeys = Object.keys(orig)
    if (localKeys.length !== origKeys.length) return true
    for (const k of localKeys) {
      const ld = local.derivedPlans[k]
      const od = orig[k]
      if (!od || ld.offset_type !== od.offset_type || ld.offset_value !== od.offset_value)
        return true
    }
    return false
  }

  function linkChanged(rt: RoomsSetupRoomType) {
    const local = links[String(rt.id)]
    if (!local) return false
    const origParent = rt.parentRoomTypeId
    if (local.parentRoomTypeId !== origParent) return true
    if (local.parentRoomTypeId == null) return false
    const origType = rt.parentOffsetType === 'percentage' ? 'percentage' : 'absolute'
    const origVal = rt.parentOffsetValue != null ? String(rt.parentOffsetValue) : ''
    return local.offsetType !== origType || local.offsetValue !== origVal
  }

  const changes = useMemo(() => {
    const baseRateRooms: RoomsSetupRoomType[] = []
    const boundRooms: RoomsSetupRoomType[] = []
    const leadRooms: RoomsSetupRoomType[] = []
    const linkedNow: RoomsSetupRoomType[] = []
    const unlinkedNow: RoomsSetupRoomType[] = []
    for (const rt of roomTypes) {
      if (rateChanged(rt)) baseRateRooms.push(rt)
      if (boundChanged(rt)) boundRooms.push(rt)
      if (leadChanged(rt)) leadRooms.push(rt)
      if (linkChanged(rt)) {
        const nowLinked = links[String(rt.id)]?.parentRoomTypeId != null
        if (nowLinked) linkedNow.push(rt)
        else unlinkedNow.push(rt)
      }
    }
    return { baseRateRooms, boundRooms, leadRooms, linkedNow, unlinkedNow }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rates, bounds, leadPlans, links, baseRates, roomTypes])

  const isDirty =
    changes.baseRateRooms.length > 0 ||
    changes.boundRooms.length > 0 ||
    changes.leadRooms.length > 0 ||
    changes.linkedNow.length > 0 ||
    changes.unlinkedNow.length > 0

  // Navigation guard: warn on browser tab close
  useEffect(() => {
    if (!isDirty || saving) return
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [isDirty, saving])

  // Navigation guard: warn on Inertia navigation (sidebar links, tab switches)
  useEffect(() => {
    return router.on('before', (event) => {
      if (!isDirty || savingRef.current) return
      if (!window.confirm('You have unsaved changes. Leave anyway?')) {
        event.preventDefault()
      }
    })
  }, [isDirty])

  // The modal stages everything locally and hands back one draft on "Apply".
  function handleApplyRoom(roomId: number, draft: RoomDraft) {
    const rt = roomTypes.find((r) => r.id === roomId)
    if (!rt) return
    setRates((prev) => ({
      ...prev,
      [rt.eviivoRoomTypeId]: { ...prev[rt.eviivoRoomTypeId], ...draft.rates },
    }))
    setBounds((prev) => ({ ...prev, [String(roomId)]: { ...draft.bounds } }))
    setLinks((prev) => ({ ...prev, [String(roomId)]: { ...draft.link } }))
  }

  // Lead plan stays live (its derived-rates flow is a separate nested modal).
  // The new lead leaves the follow-on list: posting its old offset made the save demote the lead it
  // had just set, leaving the room with no lead plan (the server now ignores it too).
  function handleLeadPlanChange(roomTypeId: number, planId: number) {
    setLeadPlans((prev) => {
      const { [String(planId)]: _ownOffset, ...derivedPlans } = prev[String(roomTypeId)]?.derivedPlans || {}
      return { ...prev, [String(roomTypeId)]: { leadRatePlanId: planId, derivedPlans } }
    })
  }

  function handleDerivedPlansChange(
    roomTypeId: number,
    derivedPayload: Record<string, DerivedPlanInput>
  ) {
    setLeadPlans((prev) => ({
      ...prev,
      [String(roomTypeId)]: {
        leadRatePlanId: prev[String(roomTypeId)]?.leadRatePlanId ?? null,
        derivedPlans: derivedPayload,
      },
    }))
    setDerivedRatePlansRoomId(null)
  }

  function handleOpenDerivedRates(roomTypeId: number) {
    setDerivedRatePlansRoomId(roomTypeId)
  }

  // Build a "view" of the room for the derived modal reflecting local state
  const modalRoom = useMemo(() => {
    if (!derivedRatePlansRoom || !derivedRatePlansLocal) return null
    const effectiveLeadId = derivedRatePlansLocal.leadRatePlanId
    if (effectiveLeadId == null) return null
    return {
      roomTypeId: derivedRatePlansRoom.id,
      leadPlanId: effectiveLeadId,
      ratePlans: derivedRatePlansRoom.ratePlans,
      initialDerivedPlans: derivedRatePlansLocal.derivedPlans,
    }
  }, [derivedRatePlansRoom, derivedRatePlansLocal])

  // --- Tree structure (reflects staged links) ---
  const stagedParentId = (rt: RoomsSetupRoomType) =>
    links[String(rt.id)]?.parentRoomTypeId ?? null

  const byName = (a: RoomsSetupRoomType, b: RoomsSetupRoomType) =>
    a.name.localeCompare(b.name)

  // ONE PLAIN LIST, the way an ordinary hotel's page reads: the building's rooms alphabetically, each
  // linked child indented under its parent. The server names a room's listing only where two rows
  // share a name, so the sort needs nothing else.
  const orderedRooms = useMemo(
    () => [...roomTypes].sort(byName),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [roomTypes]
  )

  // THE LADDER FOLLOWS THE ACTUAL PARENT, keyed on identity, never on which row sits above which.
  // A link may cross listings at a combined inventory; the list has no headings, so the child sits
  // directly beneath the room it follows like any other child. A room whose parent is not a row here
  // (an Added room following a Same room) is drawn as a root and names its parent in words. Staged
  // links, so the ladder reshapes the moment a parent is chosen, before anything is saved.
  const ladder = useMemo(
    () => nestRoomRows(orderedRooms, stagedParentId, { crossHotelLinks: 'nest' }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [orderedRooms, links]
  )

  // Valid parents: a per-room room, not itself, that is itself independent (one level of linking
  // only), from the building's rooms by their names on this page. Eligibility is unchanged; the
  // backend re-validates on save. A Same room is not a row, so it is never offered as a new parent;
  // a room that already followed one keeps it on its list, by the name the server gave it, so the
  // dialog still shows the parent it has.
  const parentOptionsFor = (rt: RoomsSetupRoomType) => {
    const options: { id: number; name: string }[] = orderedRooms
      .filter(
        (cand) =>
          cand.id !== rt.id && cand.priceStrategy === 'per_room' && stagedParentId(cand) == null
      )
      .map((c) => ({ id: c.id, name: c.name }))
    if (rt.parentRoomTypeId != null && rt.parentName && !options.some((o) => o.id === rt.parentRoomTypeId)) {
      options.push({ id: rt.parentRoomTypeId, name: rt.parentName })
    }
    return options
  }

  // ONE PAYLOAD FOR THE REVIEW AND THE SAVE (change safety §5.17.1): both send the same bytes, so the
  // server reviews exactly the save the operator confirms. Only the rooms on this page are posted; the
  // server keeps every other room's stored values (a Same room's, a hidden listing's) as they are.
  function buildRoomsSetupPayload() {
    const leadPlansPayload: Record<
      string,
      { lead_rate_plan_id: number; derived_plans: Record<string, DerivedPlanInput> }
    > = {}
    for (const [rtId, state] of Object.entries(leadPlans)) {
      if (state.leadRatePlanId != null) {
        leadPlansPayload[rtId] = {
          lead_rate_plan_id: state.leadRatePlanId,
          derived_plans: state.derivedPlans,
        }
      }
    }

    const linksPayload: Record<string, Record<string, unknown>> = {}
    for (const rt of [...changes.linkedNow, ...changes.unlinkedNow]) {
      const l = links[String(rt.id)]
      linksPayload[String(rt.id)] =
        l.parentRoomTypeId == null
          ? { parent_room_type_id: null }
          : {
              parent_room_type_id: l.parentRoomTypeId,
              parent_offset_type: l.offsetType,
              parent_offset_value: l.offsetValue,
            }
    }

    return {
      base_rates: rates,
      bounds,
      lead_plans: leadPlansPayload,
      links: linksPayload,
    }
  }

  // "Review & Save": the server reviews the draft first. A refused review's sentences carry the room
  // they name, so they land in the same anchored panel a refused save uses, as well as the dialog.
  function openReview() {
    const payload = buildRoomsSetupPayload()
    review.open(`${property.basePath}/pricing-rules/rooms-setup/review`, payload, () => save(payload), {
      onRefused: (errors) => setSaveErrors(errors),
    })
  }

  function save(payload: ReturnType<typeof buildRoomsSetupPayload>) {
    // Set before the visit: the navigation guard reads it on the PATCH's own `before` event.
    savingRef.current = true
    setSaving(true)

    return inertiaSave(
      'patch',
      `${property.basePath}/pricing-rules/rooms-setup`,
      payload,
      {
        // Keep the operator where they were: the error panel renders by the save bar and
        // scrolls itself into view; losing scroll position would hide it.
        preserveScroll: true,
        onStart: () => setSaveErrors(null),
        onError: (errors) => {
          setSaveErrors(
            parseRoomsSetupErrors((errors as Record<string, unknown>).roomsSetup) ?? [
              { roomTypeId: null, message: 'Your changes could not be saved. Please try again.' },
            ]
          )
        },
        onSuccess: () => setSaveErrors(null),
        onFinish: () => {
          savingRef.current = false
          setSaving(false)
        },
      }
    )
  }

  const editingLink = editingRoom
    ? links[String(editingRoom.id)] ?? {
        parentRoomTypeId: null,
        offsetType: 'percentage' as const,
        offsetValue: '',
      }
    : null

  function RoomRow({
    rt,
    depth,
  }: {
    rt: RoomsSetupRoomType
    depth: number
  }) {
    const link = links[String(rt.id)]
    const isLinked = link?.parentRoomTypeId != null
    // The room it follows is not a row of this page (a Same room an Added room follows): the row is a
    // root, so it names that room in the server's words, for as long as the staged link still points at it.
    const followsUnlisted =
      isLinked && rt.parentName != null && link!.parentRoomTypeId === rt.parentRoomTypeId ? rt.parentName : null
    const isPerRoom = rt.priceStrategy === 'per_room'
    const bound = bounds[String(rt.id)] || { min: '', max: '' }

    // Pricing visibility — pricingStatus is backend-derived (mirrors the
    // compute pipeline's skip rules); here we only reflect staged edits so a
    // badge resolves as soon as the operator fixes it, before saving.
    const stagedRates = rates[rt.eviivoRoomTypeId] || {}
    const hasStagedBaseRate = Object.values(stagedRates).some(
      (v) => (v ?? '').trim() !== '' && Number.isFinite(Number(v))
    )
    const noActivePlans = rt.pricingStatus === 'no_active_plans'
    const missingBaseRate =
      rt.pricingStatus === 'missing_base_rate' && !hasStagedBaseRate && !isLinked
    const stagedLeadId = leadPlans[String(rt.id)]?.leadRatePlanId ?? null
    const showLeadNudge = rt.pricingStatus === 'priced' && stagedLeadId == null

    // Non-lead plans with no staged offset yet, once a lead is chosen — the pipeline
    // prices neither, so the engine pushes no rate for them. Computed from STAGED state (not
    // the backend count) so the badge clears the moment the operator sets offsets in the
    // Derived Rate Plans dialog, before saving. Mirrors the showLeadNudge/missingBaseRate
    // philosophy on this page.
    const stagedDerived = leadPlans[String(rt.id)]?.derivedPlans ?? {}
    const unpricedCount =
      stagedLeadId == null
        ? 0
        : rt.ratePlans.filter((rp) => rp.id !== stagedLeadId && !(String(rp.id) in stagedDerived)).length

    // Floor / Ceiling — staged bound values, rendered with the shared currency
    // formatter only. No rate math.
    const boundCell = (raw: string) => {
      const v = (raw ?? '').trim()
      if (v === '' || !Number.isFinite(Number(v))) {
        return <span className="text-sm text-muted-foreground/50">—</span>
      }
      return (
        <span className="tabular-nums text-sm text-muted-foreground">
          {formatMoneySym(Number(v), sym)}
        </span>
      )
    }

    // Base Rate — for a linked child it carries the signed +/- offset vs the
    // parent; for an independent room it's the staged base rate (a single
    // value, or the low–high spread for per-occupancy rooms). String/format
    // only — no derivation of monetary values.
    let baseRateNode: React.ReactNode
    if (isLinked) {
      const raw = (link!.offsetValue ?? '').trim() || '0'
      const offsetNum = Number(raw)
      const negative = offsetNum < 0
      const bare = (negative ? raw.slice(1) : raw).replace(/^\+/, '')
      const label =
        link!.offsetType === 'percentage'
          ? `${negative ? '-' : '+'}${bare}%`
          : `${negative ? '-' : '+'}${sym}${bare}`

      // Live hint only: apply the staged offset to the parent's *staged*
      // (possibly unsaved) base rate so the operator sees the resulting price
      // while configuring. This is an on-screen preview — NOT the rate that
      // gets computed/pushed (a linked room actually anchors to the parent's
      // nightly computed rate). Client-side display math is intentional here.
      const parent = roomTypes.find((r) => r.id === link!.parentRoomTypeId)
      const parentBaseRaw = parent
        ? (rates[parent.eviivoRoomTypeId]?.['1'] ?? '').trim()
        : ''
      const parentBase = Number(parentBaseRaw)
      const hasPreview =
        parentBaseRaw !== '' && Number.isFinite(parentBase) && Number.isFinite(offsetNum)
      const resolvedRaw = !hasPreview
        ? NaN
        : link!.offsetType === 'percentage'
          ? parentBase * (1 + offsetNum / 100)
          : parentBase + offsetNum
      // Normalise float noise (e.g. 110.00000000000001) — it's just a hint.
      const resolved = Math.round(resolvedRaw * 100) / 100

      const offsetText = (
        <span className={`font-semibold ${negative ? 'text-rose-600' : 'text-emerald-600'}`}>
          {label}
        </span>
      )
      baseRateNode = hasPreview ? (
        <span
          className="inline-flex items-baseline justify-end gap-1 tabular-nums text-sm"
          title="Parent's (staged) base rate with this offset applied — a live preview, not the final computed rate"
        >
          {offsetText}
          <span className="text-muted-foreground/50">·</span>
          <span className="font-semibold text-foreground">
            {formatMoneySym(resolved, sym)}
          </span>
        </span>
      ) : (
        <span className="tabular-nums text-sm" title="Price offset vs parent room">
          {offsetText}
        </span>
      )
    } else {
      const rrates = rates[rt.eviivoRoomTypeId] || {}
      const upto = isPerRoom ? 1 : rt.maxOccupancy
      const vals: number[] = []
      for (let occ = 1; occ <= upto; occ++) {
        const raw = (rrates[String(occ)] ?? '').trim()
        if (raw !== '' && Number.isFinite(Number(raw))) vals.push(Number(raw))
      }
      if (vals.length === 0) {
        // MISSING BASE RATE — ONE ACTION, ON THE COLUMN'S OWN BASELINE.
        //
        // This used to be an oversized pill ("Suggested £89 · from 312 stays") stacked above a
        // separate "Set base rate" button: two controls for one decision, wrapping onto three
        // lines and towering over the `£90` in the row beneath. It is now a single amber action
        // that sits on the same right-aligned, tabular-nums, text-sm baseline as every priced
        // cell, and reads in one line that cannot wrap.
        //
        // The amber is the row's only "this needs you" signal now that the name-level
        // "No base rate" badge is gone — the fact lives where the missing number would be.
        // The suggestion is folded INTO the verb ("Set £89"), because opening the editor with
        // the amount pre-typed IS the act of setting it; the sample size stays because a
        // suggested price with no evidence is just a number. Clicking still only opens the
        // editor — nothing stages until Apply, nothing saves until Review & Save. A room with
        // no suggestion gets the same control without a figure, and the row itself remains a
        // click target that opens the editor empty.
        const suggestion = baseRateSuggestions[String(rt.id)]
        const suggestedAmount = missingBaseRate ? suggestion?.suggestedAmount ?? null : null
        baseRateNode = missingBaseRate ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              if (suggestedAmount != null) openEditorWithSuggestion(rt.id)
              else setEditingRoomId(rt.id)
            }}
            title={
              suggestedAmount != null
                ? "From this room's own bookings over the last 12 months. Opens the room editor with this amount filled in — nothing saves until Review & Save."
                : 'Opens the room editor — nothing saves until Review & Save.'
            }
            className="-mr-1 inline-flex items-baseline gap-1 whitespace-nowrap rounded-md px-1 py-0.5 text-sm tabular-nums text-amber-700 transition-colors hover:bg-amber-50"
          >
            {suggestedAmount != null ? (
              <>
                <span className="font-semibold">Set {formatMoneySym(suggestedAmount, sym)}</span>
                <span className="text-amber-700/40">·</span>
                <span className="text-xs text-amber-700/70">
                  {sampleEvidenceLabel(suggestion!.sampleSize)}
                </span>
              </>
            ) : (
              <span className="font-semibold">Set base rate</span>
            )}
          </button>
        ) : (
          <span className="text-sm text-muted-foreground/50">—</span>
        )
      } else {
        const lo = Math.min(...vals)
        const hi = Math.max(...vals)
        baseRateNode = (
          <span className="tabular-nums text-sm font-semibold text-foreground">
            {lo === hi
              ? formatMoneySym(lo, sym)
              : `${formatMoneySym(lo, sym)}–${formatMoneySym(hi, sym)}`}
          </span>
        )
      }
    }

    return (
      <TableRow
        id={`room-${rt.id}`}
        className="cursor-pointer transition-colors hover:bg-muted/40"
        onClick={() => setEditingRoomId(rt.id)}
      >
        <TableCell>
          <div className="flex items-center gap-2" style={{ paddingLeft: depth * 28 }}>
            {depth > 0 ? (
              <svg
                viewBox="0 0 24 24"
                className="h-4 w-4 shrink-0 text-primary/70"
                fill="currentColor"
                aria-hidden
              >
                <path d={LINK_GLYPH} />
              </svg>
            ) : (
              <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-primary/8">
                <BedDouble className="h-3.5 w-3.5 text-primary" />
              </div>
            )}
            <div className="min-w-0">
              <span className="flex items-center gap-2">
                <span className="truncate text-sm font-semibold text-foreground">
                  {rt.name}
                </span>
                {noActivePlans && (
                  <span className="inline-flex shrink-0 items-center rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-700">
                    Not priced
                  </span>
                )}
                {/* No "No base rate" badge here. The Base Rate column already says it, in the
                    place the missing number would be, with the action attached — a badge beside
                    the name was the same fact a second time. The "N plans not priced" badge
                    below is NOT that fact: it is rate plans with no offset, which no column
                    shows. */}
                {showLeadNudge && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation()
                      setEditingRoomId(rt.id)
                    }}
                    title="Derived rate plan offsets can't anchor without a lead plan. Pick one in the room editor."
                    className="inline-flex shrink-0 items-center rounded-full border border-dashed border-border px-2 py-0.5 text-[10px] font-medium text-muted-foreground transition-colors hover:border-primary/40 hover:text-primary"
                  >
                    No lead plan set
                  </button>
                )}
                {unpricedCount > 0 && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation()
                      setDerivedRatePlansRoomId(rt.id)
                    }}
                    title="These plans have no offset yet, so the engine doesn't price them. Set how each follows the lead — a discount, a premium, or the same price."
                    className="inline-flex shrink-0 items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-700 transition-colors hover:bg-amber-200"
                  >
                    {unpricedCount} {unpricedCount === 1 ? 'plan' : 'plans'} not priced
                  </button>
                )}
              </span>
              {/* THE ROOM'S SMALL PRINT: size, then how it is priced. The pricing mode used to be
                  a column of loud blue/violet pills costing the table horizontal space it needed;
                  here it is calm muted text like the units beside it. Per Occupancy carries a
                  little more weight than Per Room because it is the exceptional case and a real
                  pricing distinction (its base rate is a spread, and it cannot be linked to a
                  parent). A room following a room that is not on this page says which, last. */}
              <p className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-xs text-muted-foreground">
                <span>
                  {rt.totalUnits} {rt.totalUnits === 1 ? 'unit' : 'units'} · Max{' '}
                  {rt.maxOccupancy}
                </span>
                <span aria-hidden className="text-muted-foreground/40">
                  ·
                </span>
                <span
                  className={isPerRoom ? undefined : 'font-medium text-foreground'}
                  title={
                    isPerRoom
                      ? 'One price for the room, whatever the occupancy.'
                      : 'Priced per occupancy — a base rate for each number of guests. Per-occupancy rooms cannot be linked to a parent room.'
                  }
                >
                  {isPerRoom ? 'Per Room' : 'Per Occupancy'}
                </span>
                {followsUnlisted && (
                  <>
                    <span aria-hidden className="text-muted-foreground/40">
                      ·
                    </span>
                    <span className="inline-flex min-w-0 items-center gap-1 text-primary/80">
                      <svg viewBox="0 0 24 24" className="h-3 w-3 shrink-0" fill="currentColor" aria-hidden>
                        <path d={LINK_GLYPH} />
                      </svg>
                      <span className="break-words">Follows {followsUnlisted}</span>
                    </span>
                  </>
                )}
              </p>
              {noActivePlans && (
                <p className="mt-0.5 max-w-sm text-xs leading-relaxed text-muted-foreground">
                  No active rate plans in your PMS — this room isn&apos;t priced.
                  Manage its plans there.
                </p>
              )}
            </div>
          </div>
        </TableCell>
        {/* A room Eviivo has blocked (no active plans) isn't priced — its
            numbers are stale context, so render them at whisper level. */}
        <TableCell className={`text-right ${noActivePlans ? 'opacity-40' : ''}`}>
          {boundCell(bound.min)}
        </TableCell>
        <TableCell className={`text-right ${noActivePlans ? 'opacity-40' : ''}`}>
          {baseRateNode}
        </TableCell>
        <TableCell className={`text-right ${noActivePlans ? 'opacity-40' : ''}`}>
          {boundCell(bound.max)}
        </TableCell>
        <TableCell className="w-8 text-right">
          <ChevronRight className="ml-auto h-4 w-4 text-muted-foreground/50" />
        </TableCell>
      </TableRow>
    )
  }

  return (
    <OperatorLayout title="Rooms Setup" currentUser={currentUser} property={property} properties={properties}>
      <div className="space-y-6">
        <PricingRulesNav
          propertyBasePath={property.basePath}
          activeTab="rooms-setup"
          help={{
            content:
              "Configure each room's pricing. Rooms are priced independently by default; you can optionally link a room to a parent so it tracks the parent's price by a fixed difference.",
            learnMore: '/guides/base-rates',
          }}
        />

        <div className="mx-auto max-w-4xl space-y-6">
          {/* Provenance of the table below, plus the way to refresh it. The stale-structure
              warning banner links operators HERE expecting a sync control, so one has to exist.
              Freshness is the server's tier (SyncHealth) — never computed from the timestamp. */}
          {roomTypes.length > 0 && (
            <div
              className="animate-slide-in flex flex-wrap items-center justify-between gap-x-4 gap-y-2 px-1"
              style={{ animationDelay: '50ms' }}
            >
              <StructureSyncMeta
                lastSyncedAt={structureSync.lastSyncedAt}
                freshness={structureSync.freshness}
              />
              <div className="flex items-center gap-2">
                {/* Layered, not embedded: the count-over-time record lives on its own page
                    (Operator::RoomHistoriesController), one hop away. */}
                <Link
                  href={`${property.basePath}/rooms/history`}
                  className="inline-flex h-9 items-center rounded-lg border border-border/60 bg-background px-3 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted/40 hover:text-foreground"
                >
                  <History className="mr-1.5 h-3.5 w-3.5" />
                  Room count history
                </Link>
                {roomMappingHref && (
                  <Link
                    href={roomMappingHref}
                    className="inline-flex h-9 items-center rounded-lg border border-border/60 bg-background px-3 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted/40 hover:text-foreground"
                  >
                    <Link2 className="mr-1.5 h-3.5 w-3.5" />
                    Room mapping
                  </Link>
                )}
                {structureSync.canSync && (
                  <StructureSyncControl syncPath={structureSync.syncPath} variant="inline" />
                )}
              </div>
            </div>
          )}

          {roomTypes.length > 0 ? (
            <div
              className="animate-slide-in overflow-hidden rounded-xl border border-border/60 bg-white shadow-sm"
              style={{ animationDelay: '100ms' }}
            >
              <Table>
                <TableHeader>
                  {/* The freed space goes to Room, which is the column that wanted it (long names,
                      the indented ladder, the meta line). The three price columns are pinned so
                      they stay a tight, comparable numeric block on the right instead of drifting
                      apart — Base Rate is the widest because an unpriced row spends its width on
                      "Set £89 · 312 stays". */}
                  <TableRow>
                    <TableHead>Room</TableHead>
                    <TableHead className="w-[104px] text-right">Floor</TableHead>
                    <TableHead className="w-[168px] text-right">Base Rate</TableHead>
                    <TableHead className="w-[104px] text-right">Ceiling</TableHead>
                    <TableHead className="w-8" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {ladder.map(({ row, depth }) => (
                    <RoomRow key={row.id} rt={row} depth={depth} />
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div
              className="animate-slide-in rounded-xl border border-dashed border-border bg-white p-10 text-center shadow-sm"
              style={{ animationDelay: '100ms' }}
            >
              <BedDouble className="mx-auto h-8 w-8 text-muted-foreground/40" />
              <p className="mt-3 text-sm font-medium text-foreground">No room types found</p>
              {/* This copy used to tell the operator to "run a structure sync" while giving them
                  no way to run one. It now carries the control itself. */}
              <p className="mt-1 text-xs text-muted-foreground">
                Room types are read from your PMS.{' '}
                {structureSync.lastSyncedAt === null
                  ? 'This property has never been synced.'
                  : `Last synced ${getRelativeTime(structureSync.lastSyncedAt)}.`}
              </p>
              {structureSync.canSync ? (
                <div className="mt-4 flex justify-center">
                  <StructureSyncControl
                    syncPath={structureSync.syncPath}
                    variant="inline"
                    label="Sync rooms & rate plans"
                  />
                </div>
              ) : (
                <p className="mt-4 text-xs text-muted-foreground">
                  Ask an account owner or manager to sync this property from your PMS.
                </p>
              )}
            </div>
          )}

          {/* Failed save — inline, anchored to the rooms it names, staged edits intact. */}
          {saveErrors && (
            <div ref={saveErrorRef}>
              <RoomsSetupSaveError
                items={saveErrors}
                roomTypes={roomTypes}
                onDismiss={() => setSaveErrors(null)}
              />
            </div>
          )}

          {/* Save bar */}
          <div className="flex items-center justify-end gap-3">
            {isDirty && !saving && (
              <span className="text-xs font-medium text-amber-600">
                You have unsaved changes
              </span>
            )}
            <Button
              type="button"
              disabled={!isDirty || saving}
              className="rounded-lg px-6"
              onClick={openReview}
            >
              {saving ? 'Saving...' : 'Review & Save'}
            </Button>
          </div>
        </div>
      </div>

      {/* Polymorphic room editor — keyed per room so each open is a fresh,
          self-contained draft (no page re-render while editing). */}
      {editingRoom && editingLink && (
        <RoomTypeModal
          key={editingRoom.id}
          open={editingRoomId != null}
          onOpenChange={(open) => {
            if (!open) {
              setEditingRoomId(null)
              setSuggestionPrefillRoomId(null)
            }
          }}
          room={editingRoom}
          currencySymbol={sym}
          rates={editorRatesFor(editingRoom)}
          bounds={bounds[String(editingRoom.id)] || { min: '', max: '' }}
          link={editingLink}
          leadRatePlanId={
            leadPlans[String(editingRoom.id)]?.leadRatePlanId ?? editingRoom.leadRatePlanId
          }
          parentOptions={parentOptionsFor(editingRoom)}
          derivedPlanCount={
            Object.keys(leadPlans[String(editingRoom.id)]?.derivedPlans || {}).length
          }
          onApply={handleApplyRoom}
          onLeadPlanChange={handleLeadPlanChange}
          onOpenDerivedRates={handleOpenDerivedRates}
        />
      )}

      {/* Derived rate plans modal */}
      {modalRoom && (
        <DerivedRatePlansModal
          open={derivedRatePlansRoomId != null}
          onOpenChange={(open) => {
            if (!open) setDerivedRatePlansRoomId(null)
          }}
          roomTypeId={modalRoom.roomTypeId}
          leadPlanId={modalRoom.leadPlanId}
          ratePlans={modalRoom.ratePlans}
          initialDerivedPlans={modalRoom.initialDerivedPlans}
          currencySymbol={sym}
          onSave={handleDerivedPlansChange}
        />
      )}

      {review.dialog}
    </OperatorLayout>
  )
}
